/**
 * Environment Variable Validation Utility
 * 
 * This module provides comprehensive validation for all environment variables
 * required by the HomePH Real Estate application. It ensures that all
 * necessary configuration is present before the application starts.
 */

interface EnvValidationResult {
  isValid: boolean;
  missing: string[];
  invalid: string[];
  warnings: string[];
  errors: string[];
}

interface EnvVariable {
  key: string;
  required: boolean;
  pattern?: RegExp;
  validator?: (value: string) => boolean;
  description: string;
  example?: string;
}

/**
 * Environment variable definitions with validation rules
 */
const ENV_VARIABLES: EnvVariable[] = [
  // Supabase Configuration (Required)
  {
    key: 'VITE_SUPABASE_URL',
    required: true,
    pattern: /^https:\/\/[a-zA-Z0-9-]+\.supabase\.co$/,
    description: 'Supabase project URL',
    example: 'https://your-project-id.supabase.co'
  },
  {
    key: 'VITE_SUPABASE_ANON_KEY',
    required: true,
    pattern: /^eyJ[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/,
    description: 'Supabase anonymous key (starts with eyJ...)',
    example: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
  },
  {
    key: 'VITE_SUPABASE_SERVICE_ROLE_KEY',
    required: false, // Only required for admin scripts
    pattern: /^eyJ[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/,
    description: 'Supabase service role key (for admin operations)',
    example: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
  },

  // S3 Storage Configuration (Required)
  {
    key: 'VITE_S3_ACCESS_KEY',
    required: true,
    pattern: /^[a-f0-9]{32}$/,
    description: 'Supabase Storage S3 access key (32 characters)',
    example: 'fe55dd90fc69fdfe3e616c512d4f2770'
  },
  {
    key: 'VITE_S3_SECRET_KEY',
    required: true,
    pattern: /^[a-f0-9]{64}$/,
    description: 'Supabase Storage S3 secret key (64 characters)',
    example: 'f3442cc8f9cf07314993e5e2257a68f21b874209ae87e4617b868967bed7c104'
  },
  {
    key: 'VITE_S3_REGION',
    required: true,
    pattern: /^[a-z]{2}-[a-z]+-\d+$/,
    description: 'AWS S3 region',
    example: 'us-west-1, eu-west-1, ap-southeast-1'
  },
  {
    key: 'VITE_S3_BUCKET',
    required: true,
    pattern: /^[a-z0-9-]+$/,
    description: 'S3 bucket name (lowercase, hyphens allowed)',
    example: 'property-images, my-bucket'
  },
  {
    key: 'VITE_S3_ENDPOINT',
    required: true,
    pattern: /^https:\/\/[a-zA-Z0-9-]+\.storage\.supabase\.co\/storage\/v1\/s3$/,
    description: 'S3-compatible endpoint URL',
    example: 'https://your-project-id.storage.supabase.co/storage/v1/s3'
  },

  // Google Maps API (Optional)
  {
    key: 'VITE_GOOGLE_MAPS_API_KEY',
    required: false,
    pattern: /^AIza[0-9A-Za-z-_]{35}$/,
    description: 'Google Maps API key (starts with AIza...)',
    example: 'AIzaSyB...'
  },

  // Admin Authentication (Optional)
  {
    key: 'VITE_ADMIN_EMAIL',
    required: false,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    description: 'Admin dashboard email address',
    example: 'admin@yourdomain.com'
  },
  {
    key: 'VITE_ADMIN_PASSWORD_HASH',
    required: false,
    pattern: /^\$2[aby]\$\d{1,2}\$[./A-Za-z0-9]{53}$/,
    description: 'Bcrypt password hash (starts with $2a$, $2b$, or $2y$)',
    example: '$2b$12$LQv3c1yqBwlVHpPjrCM3Iu...'
  },
  {
    key: 'VITE_ADMIN_SESSION_SECRET',
    required: false,
    validator: (value: string) => value.length >= 32,
    description: 'Admin session secret (minimum 32 characters)',
    example: 'your-secret-key-change-in-production-at-least-32-chars'
  },
  {
    key: 'VITE_ADMIN_SESSION_TIMEOUT',
    required: false,
    pattern: /^\d+$/,
    validator: (value: string) => parseInt(value) >= 3600000, // Minimum 1 hour
    description: 'Admin session timeout in milliseconds (minimum 3600000)',
    example: '86400000'
  },

  // Email Configuration (Optional)
  {
    key: 'VITE_MAILGUN_API_KEY',
    required: false,
    pattern: /^key-[a-f0-9]{32}$/,
    description: 'Mailgun API key (starts with key-)',
    example: 'key-1234567890abcdef1234567890abcdef'
  },
  {
    key: 'VITE_MAILGUN_DOMAIN',
    required: false,
    pattern: /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
    description: 'Mailgun domain',
    example: 'yourdomain.com'
  },

  // Analytics (Optional)
  {
    key: 'VITE_GA_MEASUREMENT_ID',
    required: false,
    pattern: /^G-[A-Z0-9]{10}$/,
    description: 'Google Analytics 4 Measurement ID (starts with G-)',
    example: 'G-QM844LQ8BT'
  },

  // Public site URL for canonical tags, Open Graph, and JSON-LD (Optional)
  {
    key: 'VITE_PUBLIC_SITE_URL',
    required: false,
    pattern: /^https?:\/\/[^\s#]+$/,
    description: 'Public site URL origin (https in production; http allowed for local dev; no path or hash)',
    example: 'https://rentabahay.com'
  },

  // Development Settings
  {
    key: 'NODE_ENV',
    required: false,
    pattern: /^(development|production|test)$/,
    description: 'Node.js environment',
    example: 'development'
  },
  {
    key: 'VITE_APP_ENV',
    required: false,
    pattern: /^(development|staging|production)$/,
    description: 'Application environment',
    example: 'development'
  }
];

/**
 * Validate a single environment variable
 */
function validateEnvVariable(envVar: EnvVariable): { isValid: boolean; error?: string; warning?: string } {
  const value = import.meta.env[envVar.key];
  
  // Check if required variable is missing
  if (envVar.required && !value) {
    return {
      isValid: false,
      error: `Required environment variable ${envVar.key} is missing`
    };
  }

  // Skip validation for missing optional variables
  if (!value) {
    return { isValid: true };
  }

  // Validate pattern if specified
  if (envVar.pattern && !envVar.pattern.test(value)) {
    return {
      isValid: false,
      error: `Environment variable ${envVar.key} has invalid format. Expected: ${envVar.description}`
    };
  }

  // Validate with custom validator if specified
  if (envVar.validator && !envVar.validator(value)) {
    return {
      isValid: false,
      error: `Environment variable ${envVar.key} failed validation. ${envVar.description}`
    };
  }

  // Check for common issues
  if (value.includes('your_') || value.includes('example') || value.includes('placeholder')) {
    return {
      isValid: true,
      warning: `Environment variable ${envVar.key} appears to contain a placeholder value`
    };
  }

  return { isValid: true };
}

/**
 * Validate all environment variables
 */
export function validateEnvironment(): EnvValidationResult {
  const result: EnvValidationResult = {
    isValid: true,
    missing: [],
    invalid: [],
    warnings: [],
    errors: []
  };

  for (const envVar of ENV_VARIABLES) {
    const validation = validateEnvVariable(envVar);
    
    if (!validation.isValid) {
      result.isValid = false;
      result.invalid.push(envVar.key);
      if (validation.error) {
        result.errors.push(validation.error);
      }
    } else if (validation.warning) {
      result.warnings.push(validation.warning);
    }
  }

  return result;
}

/**
 * Get detailed validation report
 */
export function getValidationReport(): string {
  const validation = validateEnvironment();
  
  let report = '🔍 Environment Variable Validation Report\n';
  report += '=====================================\n\n';

  if (validation.isValid) {
    report += '✅ All required environment variables are valid!\n\n';
  } else {
    report += '❌ Environment validation failed!\n\n';
  }

  if (validation.errors.length > 0) {
    report += '🚨 Errors:\n';
    validation.errors.forEach(error => {
      report += `   • ${error}\n`;
    });
    report += '\n';
  }

  if (validation.warnings.length > 0) {
    report += '⚠️  Warnings:\n';
    validation.warnings.forEach(warning => {
      report += `   • ${warning}\n`;
    });
    report += '\n';
  }

  if (validation.invalid.length > 0) {
    report += '📋 Invalid Variables:\n';
    validation.invalid.forEach(key => {
      const envVar = ENV_VARIABLES.find(v => v.key === key);
      if (envVar) {
        report += `   • ${key}: ${envVar.description}\n`;
        if (envVar.example) {
          report += `     Example: ${envVar.example}\n`;
        }
      }
    });
    report += '\n';
  }

  // Show current values for debugging (masked for security)
  report += '🔐 Current Values:\n';
  ENV_VARIABLES.forEach(envVar => {
    const value = import.meta.env[envVar.key];
    if (value) {
      const masked = envVar.key.includes('KEY') || envVar.key.includes('SECRET') || envVar.key.includes('PASSWORD')
        ? `${value.substring(0, 8)}...${value.substring(value.length - 4)}`
        : value;
      report += `   • ${envVar.key}: ${masked}\n`;
    } else if (envVar.required) {
      report += `   • ${envVar.key}: ❌ MISSING (Required)\n`;
    } else {
      report += `   • ${envVar.key}: ⚪ Not set (Optional)\n`;
    }
  });

  return report;
}

/**
 * Throw error if environment is invalid
 */
export function requireValidEnvironment(): void {
  const validation = validateEnvironment();
  
  if (!validation.isValid) {
    console.error('🚨 Environment validation failed!');
    console.error(getValidationReport());
    
    // In production, don't crash the app - just log errors and show user-friendly message
    if (isProduction()) {
      console.error('Production mode: Continuing with limited functionality due to missing environment variables');
      
      // Show user-friendly error message in the UI
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: #ef4444;
        color: white;
        padding: 1rem;
        text-align: center;
        z-index: 9999;
        font-family: system-ui, sans-serif;
      `;
      errorDiv.innerHTML = `
        ⚠️ Application configuration incomplete. Some features may not work properly. 
        Please contact support if this persists.
      `;
      document.body.appendChild(errorDiv);
      
      // Remove after 10 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      }, 10000);
      
      return; // Don't throw error in production
    }
    
    const errorMessage = `Environment validation failed. Missing or invalid variables: ${validation.invalid.join(', ')}`;
    throw new Error(errorMessage);
  }

  // Log warnings if any
  if (validation.warnings.length > 0) {
    console.warn('⚠️  Environment validation warnings:');
    validation.warnings.forEach(warning => console.warn(`   ${warning}`));
  }

  console.log('✅ Environment validation passed');
}

/**
 * Get environment variable with validation
 */
export function getEnvVar(key: string, required: boolean = false): string {
  const value = import.meta.env[key];
  
  if (required && !value) {
    throw new Error(`Required environment variable ${key} is missing`);
  }
  
  return value || '';
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV === true;
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD === true;
}

/**
 * Get environment-specific configuration
 */
export function getEnvironmentConfig() {
  return {
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    supabaseUrl: getEnvVar('VITE_SUPABASE_URL', true),
    supabaseAnonKey: getEnvVar('VITE_SUPABASE_ANON_KEY', true),
    s3Config: {
      accessKeyId: getEnvVar('VITE_S3_ACCESS_KEY', true),
      secretAccessKey: getEnvVar('VITE_S3_SECRET_KEY', true),
      region: getEnvVar('VITE_S3_REGION', true),
      bucket: getEnvVar('VITE_S3_BUCKET', true),
      endpoint: getEnvVar('VITE_S3_ENDPOINT', true)
    }
  };
}

export default {
  validateEnvironment,
  getValidationReport,
  requireValidEnvironment,
  getEnvVar,
  isDevelopment,
  isProduction,
  getEnvironmentConfig
};
